import React from "react";
import { Box, Typography } from "@mui/material";
import { ITextFieldSubmission } from "@Interfaces";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface Props {
    response: ITextFieldSubmission;
}

/**
 * TextFieldResponseItem
 *
 * Displays a single submitted text field response.
 * Shows: responder ID, submission timestamp, and response text.
 *
 * Used in TextFieldResponsesList component for event managers to view all submissions.
 */
const TextFieldResponseItem: React.FC<Props> = ({ response }) => {
    const { i18n } = useTranslation();

    // Simplify guest ID: extract first 6 characters from hash
    const getShortGuestId = (): string => {
        if (!response.responder_id) {
            return "Guest_Anon";
        }
        const hash = response.responder_id.trim();
        return hash.length >= 6
            ? `Guest_${hash.substring(0, 6)}`
            : `Guest_${hash}`;
    };

    return (
        <Box
            sx={{
                p: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": {
                    borderBottom: "none",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                    }}
                >
                    {getShortGuestId()}
                </Typography>
                {response.submitted_at && (
                    <Typography
                        sx={{
                            fontSize: "0.75rem",
                            color: "text.disabled",
                        }}
                    >
                        {moment(response.submitted_at)
                            .locale(i18n.language)
                            .fromNow()}
                    </Typography>
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: "0.95rem",
                    color: "text.primary",
                    wordBreak: "break-word",
                    textAlign: "left",
                }}
            >
                {response.response}
            </Typography>
        </Box>
    );
};

export default TextFieldResponseItem;
